/* eslint-disable no-empty-function */
import { getImageIdentifier } from "@bluframe/mightychroma-mightytool-ui";
import { useCallback, useMemo, useState } from "react";
const DEFAULT_IMAGES = [];
export const initialContext = {
    addImage: () => { },
    editImage: () => { },
    images: DEFAULT_IMAGES,
    removeImage: () => { },
    resetImages: () => { }
};
const usePreviewsContext = () => {
    const [images, setImages] = useState(DEFAULT_IMAGES);
    /**
     * addImage
     *
     * @description Adds an image to the images array. If the image already exists,
     * it will not be added again.
     * @param {Omit<ImageSelectorImage, "id">} image - The image to be added.
     * @returns {void}
     *
     */
    const addImage = useCallback((image) => {
        setImages((prevImages) => {
            return [...prevImages, image];
        });
    }, []);
    /**
     * editImage
     *
     * @description Edits an image in the images array. If the image does not exist,
     * it will not be added.
     * @param {ImageSelectorImage} image - The image to be edited.
     * @returns {void}
     *
     */
    const editImage = useCallback((image) => {
        setImages((prevImages) => {
            const imageIdentifier = getImageIdentifier(image);
            return prevImages.map((item) => {
                const itemIdentifier = getImageIdentifier(item);
                return itemIdentifier === imageIdentifier
                    ? { ...item, originalUrl: image.originalUrl }
                    : item;
            });
        });
    }, []);
    /**
     * removeImage
     *
     * @description Removes an image from the images array.
     * @param {ImageSelectorImage} image - The service name of the image to be removed.
     * @returns {void}
     *
     */
    const removeImage = useCallback((image) => {
        setImages((prevImages) => {
            const imageIdentifier = getImageIdentifier(image);
            return prevImages.filter((item) => {
                const itemIdentifier = getImageIdentifier(item);
                return itemIdentifier !== imageIdentifier;
            });
        });
    }, []);
    /**
     * resetImages
     *
     * @description Resets the images array to the default state.
     * @returns {void}
     *
     */
    const resetImages = useCallback(() => {
        setImages(DEFAULT_IMAGES);
    }, []);
    /**
     * useMemo
     *
     * @description Memoizes context values
     * @returns {PreviewsContext} - Memoized context values.
     *
     */
    return useMemo(() => ({
        addImage,
        editImage,
        images,
        removeImage,
        resetImages
    }), [addImage, editImage, images, removeImage, resetImages]);
};
export default usePreviewsContext;
